<template>
  <div>
    <h3 class="mb-4">Sync Bracket Inventory</h3>

    <div class="vx-row mb-4">
      <div class="vx-col w-full md:w-1/3">
        <vs-input
          v-model="warehouseFilter"
          label="Filter Warehouse"
          placeholder="Search Warehouse"
          class="w-full"
        />
      </div>
    </div>

    <div v-if="inventoryData.length === 0" class="text-center py-8">
      Tidak ada data inventory yang perlu disesuaikan.
    </div>

    <vs-table v-if="inventoryData.length > 0" :data="paginatedData" class="sync-bracket-tbl">
      <template slot="thead">
        <vs-th>No.</vs-th>
        <vs-th>Warehouse</vs-th>
        <vs-th>Product ID</vs-th>
        <vs-th>Beginning Stock</vs-th>
        <vs-th>Outbound</vs-th>
        <vs-th>End Stock</vs-th>
        <vs-th>Stock WMS</vs-th>
        <vs-th>GAP</vs-th>
        <vs-th>Aksi</vs-th>
      </template>

      <template slot-scope="{ data }">
        <tr
          v-for="(row, index) in data"
          :key="index"
          :class="{ 'row-gap': row.gap !== null && row.gap !== 0 }"
        >
          <vs-td>{{ dataFrom + index }}</vs-td>
          <vs-td>{{ row.warehouse_name }}</vs-td>
          <vs-td>{{ row.product_id }}</vs-td>
          <vs-td>{{ formatNum(row.beginning_stock) }}</vs-td>
          <vs-td>{{ row.outbound === null ? '-' : formatNum(row.outbound) }}</vs-td>
          <vs-td>{{ formatNum(row.end_stock) }}</vs-td>
          <vs-td :class="{ 'text-bold-red': row.gap !== null && row.gap !== 0 }">
            {{ formatNum(row.stock_wms) }}
          </vs-td>
          <vs-td :class="{ 'text-bold-red': row.gap !== null && row.gap !== 0 }">
            {{ row.gap === null || row.gap === 0 ? '-' : formatNum(row.gap) }}
          </vs-td>
          <vs-td>
            <vs-button
              size="small"
              color="warning"
              type="filled"
              icon-pack="feather"
              icon="icon-refresh-cw"
              @click="openSyncModal(row)"
            >Sync Qty</vs-button>
          </vs-td>
        </tr>
      </template>
    </vs-table>

    <div class="flex justify-end items-center mt-4">
      <p class="mr-4 mb-0">
        <b>Showing {{ dataFrom }} to {{ dataTo }} of {{ dataTotal }} total entries</b>
      </p>
      <vs-pagination :total="totalPage" v-model="currentPage" :max="7"></vs-pagination>
    </div>

    <!-- Modal: Sync Base Qty -->
    <vs-popup
      class="sync-bracket-popup"
      title="Sync Base Qty"
      :active.sync="showSyncModal"
    >
      <div v-if="selectedRow" class="vx-col w-full">
        <p class="mb-2"><strong>Warehouse:</strong> {{ selectedRow.warehouse_name }}</p>
        <p class="mb-2"><strong>Product:</strong> {{ selectedRow.product_id }}</p>
        <p class="mb-2"><strong>Current Base Qty:</strong> {{ formatNum(selectedRow.stock_wms) }}</p>
        <p class="mb-2"><strong>End Stock:</strong> {{ formatNum(selectedRow.end_stock) }}</p>
        <p class="mb-4"><strong>GAP:</strong> {{ selectedRow.gap === 0 ? '-' : formatNum(selectedRow.gap) }}</p>

        <div class="flex justify-end">
          <vs-button
            color="primary"
            type="filled"
            icon-pack="feather"
            icon="icon-refresh-cw"
            @click="goToConfirm"
          >Sync</vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Modal: Konfirmasi -->
    <vs-popup
      class="sync-bracket-confirm-popup"
      title="Konfirmasi Sync"
      :active.sync="showConfirmModal"
    >
      <div v-if="selectedRow" class="vx-col w-full">
        <p class="mb-2">Anda yakin ingin <strong>sync</strong> inventory berikut?</p>
        <p class="mb-1"><strong>Warehouse:</strong> {{ selectedRow.warehouse_name }}</p>
        <p class="mb-1"><strong>Product:</strong> {{ selectedRow.product_id }}</p>
        <p class="mb-1"><strong>Current Base Qty:</strong> {{ formatNum(selectedRow.stock_wms) }}</p>
        <p class="mb-1"><strong>End Stock:</strong> {{ formatNum(selectedRow.end_stock) }}</p>
        <p class="mb-4"><strong>GAP:</strong> {{ selectedRow.gap === 0 ? '-' : formatNum(selectedRow.gap) }}</p>

        <div class="flex justify-end">
          <vs-button
            color="danger"
            type="border"
            class="mr-2"
            @click="showConfirmModal = false, showSyncModal = true"
          >Batal</vs-button>
          <vs-button
            color="success"
            type="filled"
            :disabled="isSubmitting"
            @click="submitSync"
          >{{ isSubmitting ? 'Memproses...' : 'Ya, Sync' }}</vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
export default {
  name: "SyncBracketInventory",
  data() {
    return {
      inventoryData: [],
      showSyncModal: false,
      showConfirmModal: false,
      isSubmitting: false,
      selectedRow: null,
      currentPage: 1,
      pageSize: 10,
      warehouseFilter: "",
    };
  },
  computed: {
    filteredData() {
      const q = this.warehouseFilter.toLowerCase().trim();
      if (!q) return this.inventoryData;
      return this.inventoryData.filter((row) =>
        row.warehouse_name.toLowerCase().includes(q)
      );
    },
    dataTotal() {
      return this.filteredData.length;
    },
    totalPage() {
      return Math.max(1, Math.ceil(this.dataTotal / this.pageSize));
    },
    dataFrom() {
      if (this.dataTotal === 0) return 0;
      return (this.currentPage - 1) * this.pageSize + 1;
    },
    dataTo() {
      return Math.min(this.currentPage * this.pageSize, this.dataTotal);
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredData.slice(start, end);
    },
  },
  watch: {
    currentPage() {
      this.showSyncModal = false;
      this.showConfirmModal = false;
    },
    warehouseFilter() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.fetchInventory();
  },
  methods: {
    formatNum(val) {
      if (val === null || val === undefined) return "-";
      return val.toLocaleString("id-ID");
    },
    fetchInventory() {
      this.$emit("loading", true);
      this.$http.get("support/adjust-inventory-preview")
        .then((res) => {
          const rows = res.data.data || res.data || [];
          this.inventoryData = rows.map((row) => {
            const beginningStock = Number(row.sum_in) || 0;
            const outbound = Math.abs(Number(row.sum_out) || 0);
            const endStock = Number(row.sum_transactions) || 0;
            const stockWms = Number(row.sum_inventories) || 0;
            return {
              warehouse_id: row.warehouse_id,
              warehouse_name: row.warehouse_code,
              product_id: row.product_code,
              beginning_stock: beginningStock,
              outbound,
              end_stock: endStock,
              stock_wms: stockWms,
              gap: stockWms - endStock,
            };
          });
        })
        .catch((error) => {
          this.$vs.notify({
            color: "danger",
            title: "Gagal",
            text: error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "Gagal mengambil data inventory.",
          });
        })
        .finally(() => {
          this.$emit("loading", false);
        });
    },
    openSyncModal(row) {
      this.selectedRow = { ...row };
      this.showSyncModal = true;
    },
    goToConfirm() {
      this.showSyncModal = false;
      this.showConfirmModal = true;
    },
    async submitSync() {
      this.isSubmitting = true;
      this.$emit("loading", true);
      try {
        await this.$http.post("support/adjust-inventory", {
          warehouse_id: this.selectedRow.warehouse_id,
        });
        this.$vs.notify({
          color: "success",
          title: "Berhasil",
          text: `Inventory untuk ${this.selectedRow.product_id} di ${this.selectedRow.warehouse_name} telah disync.`,
        });
        this.showConfirmModal = false;
        this.showSyncModal = false;
        this.fetchInventory();
      } catch (err) {
        this.$vs.notify({
          color: "danger",
          title: "Gagal",
          text: err.message
            || (err.response && err.response.data && err.response.data.message)
            || "Gagal sync inventory. Silakan coba lagi.",
        });
      } finally {
        this.isSubmitting = false;
        this.$emit("loading", false);
      }
    },
  },
};
</script>

<style scoped>
.sync-bracket-tbl >>> .row-gap {
  background-color: #fef2f2;
}
.text-bold-red {
  color: #dc2626 !important;
  font-weight: 700;
}
</style>

<style>
.sync-bracket-popup .vs-popup {
  width: 500px !important;
  max-width: 95vw;
}
.sync-bracket-confirm-popup .vs-popup {
  width: 450px !important;
  max-width: 95vw;
}
</style>
